import { useCallback, useEffect, useState } from 'react';

type Fetcher<Data, Args extends unknown[]> = (...args: Args) => Promise<Data>;

interface UseFetchOptions {
	enabled?: boolean;
}

// Base interface with common properties
interface BaseUseFetchResult<Data> {
	refetch: () => void;
}

// Disabled state - when enabled is false
interface DisabledUseFetchResult<Data> extends BaseUseFetchResult<Data> {
	data: null;
	error: null;
	loading: false;
}

// Loading state - when enabled is true and currently fetching
interface LoadingUseFetchResult<Data> extends BaseUseFetchResult<Data> {
	data: null;
	error: null;
	loading: true;
}

// Success state - when enabled is true and data was successfully fetched
interface SuccessUseFetchResult<Data> extends BaseUseFetchResult<Data> {
	data: Data;
	error: null;
	loading: false;
}

// Error state - when enabled is true but fetching failed
interface ErrorUseFetchResult<Data> extends BaseUseFetchResult<Data> {
	data: null;
	error: Error;
	loading: false;
}

// Union type representing all possible states
type UseFetchResult<Data> =
	| DisabledUseFetchResult<Data>
	| LoadingUseFetchResult<Data>
	| SuccessUseFetchResult<Data>
	| ErrorUseFetchResult<Data>;

// Union type for when enabled is true (excludes disabled state)
type EnabledUseFetchResult<Data> =
	| LoadingUseFetchResult<Data>
	| SuccessUseFetchResult<Data>
	| ErrorUseFetchResult<Data>;

// Function overloads for better type inference
function useFetch<Data, Args extends unknown[]>(
	fetcher: Fetcher<Data, Args>,
	args: Args,
	options: { enabled: false } & UseFetchOptions,
): DisabledUseFetchResult<Data>;

function useFetch<Data, Args extends unknown[]>(
	fetcher: Fetcher<Data, Args>,
	args: Args,
	options?: { enabled?: true } & UseFetchOptions,
): EnabledUseFetchResult<Data>;

function useFetch<Data, Args extends unknown[]>(
	fetcher: Fetcher<Data, Args>,
	args: Args,
	options: { enabled: boolean } & UseFetchOptions,
): UseFetchResult<Data>;

function useFetch<Data, Args extends unknown[]>(
	fetcher: Fetcher<Data, Args>,
	args: Args,
	{ enabled = true }: UseFetchOptions = {},
): UseFetchResult<Data> {
	const [data, setData] = useState<Data | null>(null);
	const [loading, setLoading] = useState<boolean>(enabled);
	const [error, setError] = useState<Error | null>(null);

	const fetchData = useCallback(async () => {
		let isActive = true;

		setData(null);
		setLoading(true);
		setError(null);

		try {
			const result = await fetcher(...args);
			if (isActive) {
				setData(result);
			}
		} catch (err) {
			if (isActive) {
				setError(err instanceof Error ? err : new Error('Unknown error'));
			}
		} finally {
			if (isActive) {
				setLoading(false);
			}
		}

		return () => {
			isActive = false;
		};
		// eslint says "React Hook useEffect has a missing dependency: 'args'". We already have spread args in the dependency array.
	}, [fetcher, setLoading, setData, setError, ...args]); // eslint-disable-line react-hooks/exhaustive-deps

	const refetch = useCallback(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		if (!enabled) {
			setLoading(false);
			setData(null);
			setError(null);
			return;
		}
		fetchData();
	}, [enabled, fetchData, setLoading, setData, setError]);

	return { data, loading, error, refetch } as UseFetchResult<Data>;
}

export default useFetch;
